import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { typography } from '../../styles/typography';
import ApplyLeave from './ApplyLeave';
import LeaveRequests from './LeaveRequests';

const LeaveManagement = ({ defaultView = 'apply' }) => {
  // Log screen view
  useScreenLogging('LeaveManagement', {
    component: 'LeaveManagement',
    defaultView,
  });
  const location = useLocation();
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState(defaultView);

  // Update active view based on route
  useEffect(() => {
    if (location.pathname === '/leave-management/apply') {
      setActiveView('apply');
    } else if (location.pathname.startsWith('/leave-management/manage')) {
      setActiveView('requests');
    } else if (location.pathname === '/leave-management') {
      // Default to apply if on base route
      setActiveView('apply');
      navigate('/leave-management/apply', { replace: true });
    }
  }, [location.pathname, navigate]);
  const [permissions, setPermissions] = useState([]);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(true);
  // Leave balance functionality removed - not available in backend yet
  // const [leaveStats, setLeaveStats] = useState(null);
  // const [isLoadingStats, setIsLoadingStats] = useState(false);

  useEffect(() => {
    fetchPermissions();
    // fetchLeaveStats(); // Commented out - leave balance not available in backend
  }, []);

  const fetchPermissions = async () => {
    try {
      // Mock permissions - all users have access to all features
      const mockPermissions = [
        'leaves.apply',
        'leaves.view_own',
        'leaves.view_all',
        'leaves.view_team',
        'leaves.decision',
      ];

      setPermissions(mockPermissions);
    } catch (error) {
      console.error('Error fetching permissions:', error);
    } finally {
      setIsLoadingPermissions(false);
    }
  };

  // Leave balance functionality removed - not available in backend yet
  // const fetchLeaveStats = async () => {
  //   setIsLoadingStats(true);
  //   try {
  //     const [balance, myRequests, teamRequests] = await Promise.all([
  //       leaveApi.getLeaveBalance(),
  //       leaveApi.getMyLeaveRequests(),
  //       leaveApi.getPendingLeaveRequests(),
  //     ]);

  //     setLeaveStats({
  //       balance,
  //       myRequests: myRequests.leave_requests || [],
  //       teamRequests: teamRequests.leave_requests || [],
  //     });
  //   } catch (error) {
  //     console.error('Error fetching leave stats:', error);
  //     // Set default stats if API fails
  //     setLeaveStats({
  //       balance: {
  //         casual_leave: 12,
  //         sick_leave: 7,
  //         earned_leave: 21,
  //         compensatory_off: 0,
  //         loss_of_pay: 0,
  //       },
  //       myRequests: [],
  //       teamRequests: [],
  //     });
  //   } finally {
  //     setIsLoadingStats(false);
  //   }
  // };

  const hasApplyLeavePermission = () => {
    return permissions.includes('leaves.apply');
  };

  const hasLeaveRequestsPermission = () => {
    return (
      permissions.includes('leaves.view_own') ||
      permissions.includes('leaves.view_all') ||
      permissions.includes('leaves.view_team') ||
      permissions.includes('leaves.decision')
    );
  };

  if (isLoadingPermissions) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // If no permissions, show a message
  if (!hasApplyLeavePermission() && !hasLeaveRequestsPermission()) {
    return (
      <div className="text-center py-4">
        <div className="text-gray-500 text-lg mb-2">🔒</div>
        <h3
          className={typography.h3}
          style={{ color: '#000000', marginBottom: '1rem' }}
        >
          Access Denied
        </h3>
        <p className={typography.p} style={{ color: '#666666' }}>
          You do not have permission to access leave management features.
        </p>
      </div>
    );
  }

  return (
    <div className="leave-management-container">
      {/* Content */}

      {/* Leave Statistics - Removed as leave balance is not available in backend yet */}
      {/* 
      {leaveStats && (
        <div className="mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card.Gradient className="p-6">
              <h5 className={typography.h5} style={{ color: '#000000', marginBottom: '1rem' }}>
                Leave Balance
              </h5>
              <div className="space-y-2">
                {Object.entries(leaveStats.balance).map(([type, balance]) => (
                  <div key={type} className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 capitalize">
                      {type.replace('_', ' ')}
                    </span>
                    <span className="font-semibold text-blue-600">{balance}</span>
                  </div>
                ))}
              </div>
            </Card.Gradient>
            <Card.Gradient className="p-6">
              <h5 className={typography.h5} style={{ color: '#000000', marginBottom: '1rem' }}>
                My Requests
              </h5>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total Requests</span>
                  <span className="font-semibold text-blue-600">{leaveStats.myRequests.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Pending</span>
                  <span className="font-semibold text-yellow-600">
                    {leaveStats.myRequests.filter(r => r.status === 'pending').length}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Approved</span>
                  <span className="font-semibold text-green-600">
                    {leaveStats.myRequests.filter(r => r.status === 'approved').length}
                  </span>
                </div>
              </div>
            </Card.Gradient>
            <Card.Gradient className="p-6">
              <h5 className={typography.h5} style={{ color: '#000000', marginBottom: '1rem' }}>
                Team Requests
              </h5>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Pending Approval</span>
                  <span className="font-semibold text-yellow-600">{leaveStats.teamRequests.length}</span>
                </div>
                <div className="text-xs text-gray-500 mt-2">Requires your attention</div>
              </div>
            </Card.Gradient>
          </div>
        </div>
      )}
      */}

      {/* Navigation Cards */}
      {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {hasApplyLeavePermission() && (
          <Card.Gradient
            className="p-6 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setActiveView('apply')}
          >
            <div className="text-center">
              <div className="text-4xl mb-4">📝</div>
              <h3
                className={typography.h3}
                style={{ color: '#000000', marginBottom: '1rem' }}
              >
                Apply for Leave
              </h3>
              <p className={typography.p} style={{ color: '#666666' }}>
                Submit a new leave request with date selection and reason
              </p>
            </div>
          </Card.Gradient>
        )}

        {hasLeaveRequestsPermission() && (
          <Card.Gradient
            className="p-6 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setActiveView('requests')}
          >
            <div className="text-center">
              <div className="text-4xl mb-4">📋</div>
              <h3
                className={typography.h3}
                style={{ color: '#000000', marginBottom: '1rem' }}
              >
                Manage Requests
              </h3>
              <p className={typography.p} style={{ color: '#666666' }}>
                View and manage your leave requests and team approvals
              </p>
            </div>
          </Card.Gradient>
        )}
      </div> */}

      <div className="min-h-96">
        {activeView === 'apply' && hasApplyLeavePermission() && <ApplyLeave />}
        {activeView === 'requests' && hasLeaveRequestsPermission() && (
          <LeaveRequests />
        )}
      </div>

      <style>{`
        .leave-management-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0rem;
        }
      `}</style>
    </div>
  );
};

export default LeaveManagement;
